import * as THREE from 'three';
import { POLYGONS } from "../Global/variables";
import { createSegmentPlane } from "../Global/functions";
import { getVertices, getEdgeDirection, getRotation } from './Helpers';
import { checkPanelsOK } from './PanelFunctions';
import { COLORS } from '../Global/materials';
import { degToRad } from 'three/src/math/MathUtils.js';
import inside from 'point-in-polygon';

// basic function that automatically populates the segment with panels
// populates simply with selected options of potrait or landsacpe  
export async function autoPopulateSegment(
  segmentIndex,
  solarPanel,
  addPanel,
  panelList,
  scene,
  camera,
  setPanelsOK,
  arePanelsOK,
  options = {}
) {

  const { panelSpacing = 1/39.37 } = options; // Default 1 inch in meters

  // Get segment plane and shape
  const plane = createSegmentPlane(segmentIndex);
  const segment = POLYGONS[segmentIndex].shape;
  const segmentVertices = getSegmentVertices(segmentIndex);

  // Get panel dimensions based on selected panel
  const data = panelList.filter((panel) => panel.id === solarPanel.id)[0];
  let panelWidth, panelHeight, area;
  
  if (data.unit === 'Metric') {
    // Convert mm to meters
    panelWidth = data.width / 1000;
    panelHeight = data.height / 1000;
    area = data.width * data.height / 1000000; // Area in square meters
  } else if (data.unit === 'Imperial') {
    // Convert inches to meters
    panelWidth = data.width / 39.37;
    panelHeight = data.height / 39.37;
    area = data.width * data.height / 1549.9969; // Area in square meters
  }
  
  // Swap dimensions if landscape orientation
  if (solarPanel.orientation === "Landscape") {
    [panelWidth, panelHeight] = [panelHeight, panelWidth];
  }

  // Calculate optimal spacing between panels (use the same spacing as in the application)
  const OFFSET = panelSpacing; // Spacing between panels in meters
  const spacedPanelWidth = panelWidth + OFFSET;
  const spacedPanelHeight = panelHeight + OFFSET;

  // Get segment orientation
  const normal = plane.normal.clone().normalize();
  const edgeDirection = getEdgeDirection(POLYGONS[segmentIndex], normal, 'horizontal');
  
  const insetVertices = shrinkPolygon(segmentVertices, 0.3);
 
  const positions = generateBestOptimizedPlacements(
    insetVertices,
    spacedPanelWidth,
    spacedPanelHeight,
    normal,
    edgeDirection,
    plane,
    segmentIndex
  );
  
  let stats = {
    totalPositions: positions.length,
    panelsPlaced: 0,
    failedPlacements: 0
  };

  // Clear existing panels in the segment
  if (POLYGONS[segmentIndex].panels.length > 0) {
      POLYGONS[segmentIndex].panels.forEach(panelData => {
          if (panelData.panel) {
              scene.remove(panelData.panel);
              if (panelData.panel.geometry) panelData.panel.geometry.dispose();
              if (panelData.panel.material) panelData.panel.material.dispose();
          }
      });
      POLYGONS[segmentIndex].panels = [];
  }

  // Try to place panels at each position
  for (const position of positions) {
    try {
      // Create panel at this position
      const panel = await createPanelAtPosition(
        position,
        panelWidth,
        panelHeight,
        area,
        solarPanel,
        data,
        scene,
        plane,
        segment,
        normal,
        edgeDirection,
        segmentIndex
      );           
        
        // Panel is valid, add it to the segment
        POLYGONS[segmentIndex].panels.push({panel: panel, area: area, id: solarPanel.id});
        
        // Add panel to UI list
        let uiData = { ...solarPanel, panel_id: panel.id };
        addPanel({ newPanel: uiData, segmentIndex: segmentIndex });
        
        
        stats.panelsPlaced++;
      
    } catch (error) {
      console.error("Error placing panel:", error);
      stats.failedPlacements++;
    }
  }

  // Validate all panels after placement
  checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
  const horizontalAxis = edgeDirection.clone().normalize();
  const verticalAxis = new THREE.Vector3().crossVectors(normal, horizontalAxis).normalize();

  for (const panel of POLYGONS[segmentIndex].panels) {
    const corners = calculatePanelCorners(panel.panel.position, panelWidth, panelHeight, horizontalAxis, verticalAxis);

    if (!areAllPanelCornersInsideSegment(
        corners,
        segmentVertices,
        panel.panel.position,
        horizontalAxis,
        verticalAxis
      )) {
      scene.remove(panel.panel)
    }
  }

  return stats;
}

function areAllPanelCornersInsideSegment(
  panelCorners,
  segmentVertices,
  origin,
  horizontalAxis,
  verticalAxis
) {
  // Create normalized basis vectors in the polygon's plane
  const u = horizontalAxis.clone().normalize();
  const v = verticalAxis.clone().normalize();

  // Project a 3D point to 2D coordinates in the polygon's plane
  function projectToPlane2D(point) {
    const relative = point.clone().sub(origin);
    return {
      x: relative.dot(u),
      y: relative.dot(v),
    };
  }

  // Perform 2D point-in-polygon check using ray casting
  function isPointInPolygon(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;

      const intersect =
        (yi > point.y) !== (yj > point.y) &&
        point.x < ((xj - xi) * (point.y - yi)) / ((yj - yi) || 1e-10) + xi;

      if (intersect) inside = !inside;
    }
    return inside;
  }

  // Project all segment vertices to 2D
  const projectedSegment = segmentVertices.map(projectToPlane2D);

  // Check each corner
  for (const corner of panelCorners) {
    const projectedCorner = projectToPlane2D(corner);
    const isInside = isPointInPolygon(projectedCorner, projectedSegment);

    if (!isInside) {
      return false;
    }
  }

  return true;
}

function shrinkPolygon(vertices, margin = 0.1) {
  const centroid = calculateCentroid(vertices);
  return vertices.map(v => {
    const dir = new THREE.Vector3().subVectors(v, centroid).normalize();
    return new THREE.Vector3().subVectors(v, dir.multiplyScalar(margin));
  });
}

function getSegmentVertices(segmentIndex) {
    const segment = POLYGONS[segmentIndex];
    
    if (segment.edges && segment.edges.length > 0) {
        const vertices = [];
        const edges = segment.setBacks.isSet  ? segment.setBacks.edgesData : segment.edges;
        
        vertices.push(edges[0].point1);
        
        for (let i = 1; i < edges.length; i++) {
            const currentEdge = edges[i];
            vertices.push(currentEdge.point1);
        }
        
        return vertices;
    }
}

function generatePlacementsFromOffset(
  segmentVertices,
  panelWidth,
  panelHeight,
  normal,
  edgeDirection,
  plane,
  segmentIndex,
  offset = { x: 0, y: 0 }
) {
  const horizontalAxis = edgeDirection.clone().normalize();
  const verticalAxis = new THREE.Vector3().crossVectors(normal, horizontalAxis).normalize();

  const centroid = calculateCentroid(segmentVertices);
  const projectedCentroid = new THREE.Vector3();
  plane.projectPoint(centroid, projectedCentroid);

  let maxDistance = 0;
  segmentVertices.forEach(vertex => {
    const distance = projectedCentroid.distanceTo(vertex);
    maxDistance = Math.max(maxDistance, distance);
  });

  const buffer = 1.2;
  const estimatedRows = Math.ceil((maxDistance * 2 * buffer) / panelHeight);
  const estimatedCols = Math.ceil((maxDistance * 2 * buffer) / panelWidth);

  const startPosition = new THREE.Vector3().copy(projectedCentroid)
    .sub(horizontalAxis.clone().multiplyScalar((estimatedCols * panelWidth) / 2))
    .sub(verticalAxis.clone().multiplyScalar((estimatedRows * panelHeight) / 2))
    .add(horizontalAxis.clone().multiplyScalar(offset.x))
    .add(verticalAxis.clone().multiplyScalar(offset.y));

  const validPositions = [];

  for (let row = 0; row < estimatedRows; row++) {
    for (let col = 0; col < estimatedCols; col++) {
      const positionCenter = new THREE.Vector3().copy(startPosition)
        .add(horizontalAxis.clone().multiplyScalar(col * panelWidth + panelWidth / 2))
        .add(verticalAxis.clone().multiplyScalar(row * panelHeight + panelHeight / 2));

      const projectedPosition = new THREE.Vector3();
      plane.projectPoint(positionCenter, projectedPosition);

      const corners = calculatePanelCorners(
        projectedPosition, 
        panelWidth, 
        panelHeight,
        horizontalAxis,
        verticalAxis
      ); 

      let obstructionOverlap = false;
      for (const obstruction of POLYGONS[segmentIndex]?.obstructions || []) {
        if (checkPanelOverlap(corners, obstruction?.cords, normal)) {
          obstructionOverlap = true;
          break;
        }
      }

      if (isPanelCompletelyInside(
        projectedPosition,
        panelWidth,
        panelHeight,
        horizontalAxis,
        verticalAxis,
        segmentVertices,
        normal,
        segmentIndex
      )) {
        validPositions.push(projectedPosition);
      }
    }
  }
  return validPositions;
}

function generateBestOptimizedPlacements(
  segmentVertices,
  panelWidth,
  panelHeight,
  normal,
  edgeDirection,
  plane,
  segmentIndex
) {

  const attempts = generateOffsets(panelWidth, panelHeight, 0.40); // Step = 25% of panel size

  let bestPositions = [];
  for (const offset of attempts) {
    const positions = generatePlacementsFromOffset(
      segmentVertices,
      panelWidth,
      panelHeight,
      normal,
      edgeDirection,
      plane,
      segmentIndex,
      offset
    );

    if (positions.length > bestPositions.length) {
      bestPositions = positions;
      
    }
  }

  return bestPositions;
}

function generateOffsets(panelWidth, panelHeight, step = 0.25) {
  const offsets = [];
  const offsetSteps = [-step, 0, step];

  for (const dx of offsetSteps) {
    for (const dy of offsetSteps) {
      offsets.push({ x: dx * panelWidth, y: dy * panelHeight });
    }
  }

  return offsets;
}

function checkPanelOverlap(panelCorners, obstructionPoints, normal) {
  if (panelCorners.length !== 4) return false;

  // Step 1: Create panel plane and 2D local basis
  const v0 = panelCorners[0];
  const v1 = panelCorners[1];
  const v2 = panelCorners[2];
  const panelNormal = new THREE.Vector3().crossVectors(
    new THREE.Vector3().subVectors(v1, v0),
    new THREE.Vector3().subVectors(v2, v0)
  ).normalize();

  const xAxis = new THREE.Vector3().subVectors(v1, v0).normalize();
  const yAxis = new THREE.Vector3().crossVectors(panelNormal, xAxis).normalize();
  const origin = v0.clone();

  // Helper to project 3D point onto panel plane 2D space
  const projectTo2D = (point) => {
    const relative = new THREE.Vector3().subVectors(point, origin);
    return {
      x: relative.dot(xAxis),
      y: relative.dot(yAxis)
    };
  };

  // Step 2: Convert panel and obstruction points to 2D
  const panel2D = panelCorners.map(projectTo2D);
  const obstruction2D = obstructionPoints.map(projectTo2D);

  // Step 3: Check for any intersection
  const buffer = 0.05; // 5 cm buffer

  for (let i = 0; i < obstruction2D.length - 1; i++) {
    const p1 = obstruction2D[i];
    const p2 = obstruction2D[i + 1];

    // Check line intersections (existing)
    for (let j = 0; j < 4; j++) {
      const q1 = panel2D[j];
      const q2 = panel2D[(j + 1) % 4];
      if (doLinesIntersect(p1, p2, q1, q2)) {
        return true;
      }
    }

    // New: Check buffer around each obstruction point
    if (isPointNearPolygon(p1, panel2D, buffer, normal)) {
      return true;
    }
  }

  return false; // No intersection
}

function doLinesIntersect(p1, p2, q1, q2) {
  const det = (a, b, c, d) => a * d - b * c;

  const dx1 = p2.x - p1.x;
  const dy1 = p2.y - p1.y;
  const dx2 = q2.x - q1.x;
  const dy2 = q2.y - q1.y;

  const delta = det(dx1, dy1, dx2, dy2);
  if (delta === 0) return false; // parallel

  const s = det(q1.x - p1.x, q1.y - p1.y, dx2, dy2) / delta;
  const t = det(q1.x - p1.x, q1.y - p1.y, dx1, dy1) / delta;

  return s >= 0 && s <= 1 && t >= 0 && t <= 1;
}

function isPointNearPolygon(point, polygon, buffer, normal) {
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const dist = pointToSegmentDistance(point, a, b);
    if (dist <= buffer) return true;
  }

  // Optionally: if point is already inside polygon
  if (isPointInPolygon(point, polygon,normal)) return true;

  return false;
}

function pointToSegmentDistance(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  if (l2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);

  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2));
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return Math.hypot(p.x - projX, p.y - projY);
}

function calculateCentroid(vertices) {
  const centroid = new THREE.Vector3(0, 0, 0);
  
  for (let i = 0; i < vertices.length; i++) {
    centroid.add(vertices[i]);
  }
  
  centroid.divideScalar(vertices.length);
  return centroid;
}

function calculatePanelCorners(center, width, height, horizontalAxis, verticalAxis) {
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  
  const topLeft = new THREE.Vector3().copy(center)
    .sub(horizontalAxis.clone().multiplyScalar(halfWidth))
    .sub(verticalAxis.clone().multiplyScalar(halfHeight));
    
  const topRight = new THREE.Vector3().copy(center)
    .add(horizontalAxis.clone().multiplyScalar(halfWidth))
    .sub(verticalAxis.clone().multiplyScalar(halfHeight));
    
  const bottomRight = new THREE.Vector3().copy(center)
    .add(horizontalAxis.clone().multiplyScalar(halfWidth))
    .add(verticalAxis.clone().multiplyScalar(halfHeight));
    
  const bottomLeft = new THREE.Vector3().copy(center)
    .sub(horizontalAxis.clone().multiplyScalar(halfWidth))
    .add(verticalAxis.clone().multiplyScalar(halfHeight));
  
  return [topLeft, topRight, bottomRight, bottomLeft];
}

function isPointInPolygon(point, polygonVertices, normal, buffer = 0.05) {
  const absNormal = new THREE.Vector3(
    Math.abs(normal.x),
    Math.abs(normal.y),
    Math.abs(normal.z)
  );

  let projectFunction;

  if (absNormal.x >= absNormal.y && absNormal.x >= absNormal.z) {
    projectFunction = (v) => [v.y, v.z]; // YZ
  } else if (absNormal.y >= absNormal.x && absNormal.y >= absNormal.z) {
    projectFunction = (v) => [v.x, v.z]; // XZ
  } else {
    projectFunction = (v) => [v.x, v.y]; // XY
  }

  const projectedPoint = projectFunction(point);
  const projectedPolygon = polygonVertices.map(projectFunction);

  // 1. Calculate the centroid of the polygon
  const centroid = projectedPolygon.reduce(
    (acc, [x, y]) => [acc[0] + x, acc[1] + y],
    [0, 0]
  ).map(coord => coord / projectedPolygon.length);

  // 2. Shrink the polygon slightly toward the centroid
  const shrunkenPolygon = projectedPolygon.map(([x, y]) => {
    const dx = x - centroid[0];
    const dy = y - centroid[1];
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length === 0) return [x, y]; // Skip if it's the centroid
    const factor = (length - buffer) / length;
    return [
      centroid[0] + dx * factor,
      centroid[1] + dy * factor
    ];
  });

  // 3. Perform point-in-polygon check using 'inside' lib
  return inside(projectedPoint, shrunkenPolygon);
}

function createPanelAtPosition(
  position,
  width,
  height,
  area,
  solarPanel,
  panelData,
  scene,
  plane,
  segment,
  normal,
  edgeDirection,
  segmentIndex
) {
  return new Promise((resolve, reject) => {
    try {
      // Create panel geometry
      const geometry = new THREE.PlaneGeometry(width, height);
      const planeNormal = new THREE.Vector3(0, 0, 1);
      const orientation = plane.normal.clone().normalize();
      
      // Calculate direction
      const direction = edgeDirection;
      const max = new THREE.Vector3(orientation.x, orientation.y, 0).normalize();
      
      // Use a small clearance value instead of complex tilt calculation
      const clearanceOffset = 0.01; // 1cm clearance above the roof surface
      
      // Load texture for the panel
      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(
        sessionStorage.getItem(panelData.id.toString()),
        (texture) => {
          // Set texture properties
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.repeat.set(1, 1);
          
          if (solarPanel.orientation === "Landscape") {
            texture.rotation = Math.PI / 2;
          }
          
          // Create panel material and mesh
          const material = new THREE.MeshStandardMaterial({ 
            map: texture, 
            roughness: 0.8, 
            side: THREE.DoubleSide, 
            depthTest: false, 
            depthWrite: false, 
            transparent: true 
          });
          
          const panel = new THREE.Mesh(geometry, material);
          panel.name = "panel";
          panel.renderOrder = 5;
          panel.frustumCulled = false;
          
          // Set panel type based on orientation
          if (solarPanel.orientation === "Portrait") {
            panel.panelType = 'potrait';
          } else {
            panel.panelType = 'landscape';
          }
          
          // FIXED: Apply rotations to align with roof surface
          let quaternion = new THREE.Quaternion();
          
          if (solarPanel.tilt && solarPanel.tilt !== 0) {
            // If there's a specific tilt, apply it relative to the roof surface
            const tiltRadians = degToRad(Number(solarPanel.tilt));
            const temp = new THREE.Quaternion().setFromUnitVectors(planeNormal, max);
            quaternion.rotateTowards(temp, tiltRadians);
          } else {
            // If no tilt specified, align with the roof surface
            quaternion.setFromUnitVectors(planeNormal, orientation);
          }
          
          panel.applyQuaternion(quaternion);
          
          const [sign, rotation] = getRotation(orientation, direction, quaternion);
          panel.applyQuaternion(rotation);
          
          // FIXED: Position the panel on the roof surface with minimal clearance
          // The position from generateOptimizedPlacements is already projected onto the plane
          // Just add a small offset along the normal for clearance
          panel.position.copy(
            position.clone().add(
              orientation.clone().multiplyScalar(clearanceOffset)
            )
          );
          
          // Create wireframe
          createWireframe(panel);
          
          // Add to scene
          scene.add(panel);
          
          resolve(panel);
        },
        undefined,
        (error) => {
          console.error('Error loading texture:', error);
          resolve(null);
        }
      );
    } catch (error) {
      console.error("Error creating panel:", error);
      resolve(null);
    }
  });
}

function createWireframe(panel) {
  const vertices = getVertices(panel);
  const last = vertices.splice(2, 1);
  vertices.push(last[0]);
  
  // Create edge points for wireframe
  const edgePoints = [];
  for (let i = 0; i < vertices.length; i++) {
    let index = (i + 1) % vertices.length;
    const point1 = vertices[i];
    const point2 = vertices[index];
    
    edgePoints.push(point1);
    edgePoints.push(point2);
  }
  
  // Create wireframe geometry
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(edgePoints.length * 3);
  
  for (let i = 0; i < edgePoints.length; i++) {
    positions[i * 3] = edgePoints[i].x;
    positions[i * 3 + 1] = edgePoints[i].y;
    positions[i * 3 + 2] = edgePoints[i].z;
  }
  
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  
  // Create wireframe material and mesh
  const material = new THREE.LineBasicMaterial({
    color: COLORS.red,
    transparent: true,
    opacity: 0
  });
  
  const wireframe = new THREE.LineSegments(geometry, material);
  wireframe.renderOrder = 6;
  panel.add(wireframe);
}

function isPanelCompletelyInside(
  panelCenter,
  panelWidth,
  panelHeight,
  horizontalAxis,
  verticalAxis,
  segmentVertices,
  normal,
  segmentIndex
) {
  const corners = calculatePanelCorners(panelCenter, panelWidth, panelHeight, horizontalAxis, verticalAxis);
  
  const pointsToCheck = [
    ...corners,
    panelCenter,
    ...samplePanelEdgePoints(corners, 50),
    ...samplePanelInteriorPoints(corners, 4)
  ];  

  // Check every point
  for (const point of pointsToCheck) {
    if (!isPointInPolygon(point, segmentVertices, normal)) {
      return false;
    }
  }
  
  // Check obstructions
  for (const obstruction of POLYGONS[segmentIndex]?.obstructions || []) {
    if (checkPanelOverlap(corners, obstruction?.cords, normal)) {
      return false;
    }
  }
  
  return true;
}

function samplePanelEdgePoints(corners, samplesPerEdge = 5) {
  const edgePoints = [];
  
  // Sample points along each of the 4 edges
  for (let i = 0; i < corners.length; i++) {
    const start = corners[i];
    const end = corners[(i + 1) % corners.length];
    
    // Sample points along this edge (excluding start/end as they're already corners)
    for (let j = 1; j < samplesPerEdge; j++) {
      const t = j / samplesPerEdge; // Parameter from 0 to 1
      const point = new THREE.Vector3().lerpVectors(start, end, t);
      edgePoints.push(point);
    }
  }
  
  return edgePoints;
}

function samplePanelInteriorPoints(corners, gridSize = 3) {
  const interiorPoints = [];
  
  // Create a grid of points inside the panel
  for (let i = 1; i < gridSize; i++) {
    for (let j = 1; j < gridSize; j++) {
      const u = i / gridSize; // 0 to 1
      const v = j / gridSize; // 0 to 1
      
      // Bilinear interpolation within the panel
      const top = new THREE.Vector3().lerpVectors(corners[0], corners[1], u);
      const bottom = new THREE.Vector3().lerpVectors(corners[3], corners[2], u);
      const point = new THREE.Vector3().lerpVectors(top, bottom, v);
      
      interiorPoints.push(point);
    }
  }
  
  return interiorPoints;
}